import React, { useEffect, useRef, useState } from 'react';
import { Quote, Star } from 'lucide-react';

// Render the strip twice back-to-back so the marquee animation
// (translate 0 → -50%) creates a seamless, endless loop.
function Strip({ reviews, ariaHidden = false }) {
  return (
    <div
      className="flex items-stretch gap-6 sm:gap-8 shrink-0 pr-6 sm:pr-8"
      aria-hidden={ariaHidden || undefined}
    >
      {reviews.map((r, i) => (
        <figure
          key={`${r.name}-${i}`}
          className="shrink-0 w-[300px] sm:w-[360px] bg-white/[0.03] border border-white/10 rounded-lg px-5 py-4 flex flex-col transition-[background-color,border-color,transform] duration-300 hover:bg-white/[0.05] hover:border-cyan-400/30 hover:-translate-y-0.5"
        >
          <div className="flex items-center gap-0.5 mb-2" aria-label={`${r.rating ?? 5} out of 5 stars`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={i < (r.rating ?? 5)
                ? 'w-3.5 h-3.5 fill-cyan-300 text-cyan-300 [filter:drop-shadow(0_0_3px_rgba(255,255,255,0.7))]'
                : 'w-3.5 h-3.5 text-white/20'} />
            ))}
          </div>
          <Quote className="w-4 h-4 text-cyan-400/70 shrink-0 mb-2" />
          <blockquote className="text-white/85 text-[13px] leading-relaxed flex-1">
            {r.quote}
          </blockquote>
          <figcaption className="mt-3 pt-3 border-t border-white/5">
            <div className="font-tech text-cyan-300 text-[11px] uppercase tracking-[0.16em]">
              {r.name}
            </div>
            <div className="text-white/55 text-[11px] mt-0.5">{r.attribution}</div>
            {r.model && (
              <div className="text-cyan-400/70 text-[10px] mt-1 italic">
                {r.model}
              </div>
            )}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

/**
 * ReviewsMarquee — scrolling bar of customer reviews.
 *
 * @param {string} title      Overline label shown above the marquee.
 * @param {Array}  reviews    Array of { quote, name, attribution }.
 * @param {"ltr"|"rtl"} direction  Scroll direction. Defaults to "ltr".
 * @param {string} testId     data-testid for the section.
 */
export default function ReviewsMarquee({
  title,
  caption,
  reviews,
  direction = 'ltr',
  testId = 'reviews-marquee',
}) {
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const idleTimer = useRef(null);
  const [, setPaused] = useState(false);
  const [stripCount, setStripCount] = useState(2);

  const wrapScroll = (el) => {
    const half = el.scrollWidth / 2;
    if (el.scrollLeft >= half) el.scrollLeft -= half;
    else if (el.scrollLeft < 0) el.scrollLeft += half;
  };

  // Pause on real interaction only (drag / touch / wheel) — NOT hover —
  // then restart after 2500ms idle.
  const pause = () => {
    pausedRef.current = true;
    setPaused(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      pausedRef.current = false;
      setPaused(false);
    }, 2500);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // Wrapping only works if the track is at least 2x the viewport wide —
    // with few reviews, two strips can be narrower than the screen and the
    // drift clamps at max scroll. Measure one strip, then render enough
    // (even count) so scrollWidth >= 2x clientWidth.
    const stripWidth = el.scrollWidth / stripCount;
    let n = Math.max(2, Math.ceil((el.clientWidth * 2) / stripWidth) + 1);
    if (n % 2 === 1) n += 1;
    if (n !== stripCount) {
      setStripCount(n);
      return;
    }
    el.scrollLeft = el.scrollWidth / 4; // seed mid-strip so users can drag both ways
    let raf;
    const tick = () => {
      if (!pausedRef.current) {
        el.scrollLeft += direction === 'ltr' ? 0.5 : -0.5;
        wrapScroll(el);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [direction, stripCount]);

  return (
    <section
      aria-label={title}
      data-testid={testId}
      className="relative bg-black border-y border-white/5 overflow-hidden py-10 sm:py-12"
    >
      {/* Centered title */}
      <div className="text-center px-5 mb-6 sm:mb-7">
        <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
          {title}
        </span>
        {caption && (
          <div className="mt-1.5 text-zinc-600 text-[10px] tracking-wide">{caption}</div>
        )}
      </div>

      {/* Draggable auto-drifting marquee — soft edge fade */}
      <div
        ref={trackRef}
        className="relative w-full overflow-x-auto overflow-y-hidden cursor-grab active:cursor-grabbing select-none touch-pan-x [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        style={{
          maskImage:
            'linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%)',
        }}
        onPointerDown={pause}
        onTouchStart={pause}
        onWheel={pause}
        onScroll={(e) => wrapScroll(e.currentTarget)}
      >
        <div className="flex w-max">
          {Array.from({ length: stripCount }).map((_, k) => (
            <Strip key={k} reviews={reviews} ariaHidden={k > 0} />
          ))}
        </div>
      </div>
    </section>
  );
}
